import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchGlobalSettings } from "../api/globalsetting";
import arrowRightUp from "../assets/images/Home/faq_down.svg";
import "../assets/css/pages/Home.css";
import "../assets/css/Responsive/Home_Responsive.css";

export default function Faq({ data }) {
    const [faq, setFaq] = useState(data || null);
    const [visibleCount, setVisibleCount] = useState(5);
    const [openIndex, setOpenIndex] = useState(0); // Track which FAQ is open
    const [closingIndex, setClosingIndex] = useState(null); // Track which FAQ is closing
    const contentRefs = useRef({}); // Store refs for each FAQ content
    const navigate = useNavigate();

    useEffect(() => {
        if (data) {
            setFaq(data);
            return;
        }

        let isMounted = true;
        fetchGlobalSettings()
            .then((res) => {
                if (!isMounted) return;
                setFaq(res.faq || null);
            })
            .catch((err) => {});

        return () => {
            isMounted = false;
        };
    }, [data]);

    // Ultra-smooth height calculation - ZERO JERK
    const setContentHeight = (index, isOpen) => {
        const contentElement = contentRefs.current[index];
        if (!contentElement) return;

        if (isOpen) {
            // STEP 1: Get current computed height (even if 0)
            const currentHeight = contentElement.offsetHeight;
            
            // STEP 2: Temporarily disable ALL transitions for measurement
            contentElement.style.transition = 'none';
            contentElement.style.visibility = 'visible';
            contentElement.style.opacity = '0'; // Keep invisible during measurement
            
            // STEP 3: Measure actual content height
            contentElement.style.height = 'auto';
            const targetHeight = contentElement.scrollHeight;
            
            // STEP 4: Instantly reset to current/starting height (prevents flash)
            contentElement.style.height = currentHeight + 'px';
            
            // STEP 5: Force reflow to apply the instant reset
            void contentElement.offsetHeight;
            
            // STEP 6: Re-enable transitions
            contentElement.style.transition = '';
            contentElement.style.opacity = ''; // Allow opacity to animate
            
            // STEP 7: Use RAF to ensure smooth start of animation
            requestAnimationFrame(() => {
                contentElement.style.height = targetHeight + 'px';
            });
        } else {
            // CLOSE: Simply animate to 0
            contentElement.style.height = '0px';
        }
    };

    // Handle FAQ item click - ultra-smooth, zero jerk
    const handleFaqClick = (index) => {
        if (openIndex === index) {
            // CLOSE: Clicking same item
            setClosingIndex(index);
            setContentHeight(index, false);
            
            setTimeout(() => {
                setOpenIndex(null);
                setClosingIndex(null);
            }, 470); // Slightly before animation ends
        } else {
            // OPEN NEW: Clicking different item
            
            // Close the currently open item if exists
            if (openIndex !== null) {
                setClosingIndex(openIndex);
                setContentHeight(openIndex, false);
            }
            
            // Open the new item
            setOpenIndex(index);
            
            // Wait for state update and CSS classes to apply
            // Using setTimeout with 0 ensures DOM is updated before animation
            setTimeout(() => {
                setContentHeight(index, true);
            }, 10); // Small delay for smooth class application
            
            // Clear closing state
            setTimeout(() => {
                setClosingIndex(null);
            }, 470);
        }
    };

    // Set initial height for first FAQ if it's open
    useEffect(() => {
        if (openIndex !== null && faq && contentRefs.current[openIndex]) {
            // Wait for DOM to be ready, then measure and set height
            const timer = setTimeout(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setContentHeight(openIndex, true);
                    });
                });
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [faq, openIndex]);

    if (!faq) return null;

    return (
        <section className="faq" data-stagger-index="0">
            <div className="canvas">
                <div className="faq__inner" data-stagger-index="1">


                    <div className="title-sec" data-stagger-index="2">
                        <div className="faq__eyebrow">{faq.eyebrow}</div>
                        <h2 className="faq__title">{faq.title}</h2>
                    </div>


                    <div className="faq__list" data-stagger-index="3">
                        {faq.items?.slice(0, visibleCount).map((item, index) => {
                            const isOpen = openIndex === index;
                            const isClosing = closingIndex === index;
                            return (
                                <div 
                                    className="faq-row" 
                                    key={index} 
                                    data-stagger-index={index + 1}
                                    onClick={() => handleFaqClick(index)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleFaqClick(index);
                                        }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className={`faq-item ${index === 0 ? "faq-item--lead" : ""} ${isOpen ? "faq-item--open" : ""} ${isClosing ? "faq-item--closing" : ""}`}>
                                        <div className="faq-item_summary">
                                            {item.question}
                                        </div>

                                        <div 
                                            ref={(el) => (contentRefs.current[index] = el)}
                                            className={`faq-item_content ${isOpen ? "faq-item_content--open" : ""} ${isClosing ? "faq-item_content--closing" : ""}`}
                                        >
                                            {item.answer}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>


                    {faq.items && faq.items.length > visibleCount && (
                        <div className="faq_cta" data-stagger-index={visibleCount + 1}>
                            <a
                                className="pill"
                                href="/faq"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate("/faq");
                                }}
                            >
                                {faq.cta_text || "View All Frequently Asked Questions"}

                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.85355 2.64645C5.756 2.56425 5.63155 2.51631 5.50245 2.51832C5.37339 2.52028 5.251 2.57126 5.16113 2.66113C5.07126 2.751 5.02028 2.87339 5.01832 3.00245C5.01631 3.13155 5.06425 3.256 5.14645 3.35355C5.29897 3.53436 5.4522 3.71446 5.60614 3.89386C6.99159 5.50841 8.43431 7.06569 9.93431 8.56569V7.43431C8.43431 8.93431 6.99159 10.4916 5.60614 12.1061C5.4522 12.2855 5.29897 12.4656 5.14645 12.6464C5.06425 12.744 5.01631 12.8684 5.01832 12.9976C5.02028 13.1266 5.07126 13.249 5.16113 13.3389C5.251 13.4287 5.37339 13.4797 5.50245 13.4817C5.63155 13.4837 5.756 13.4357 5.85355 13.3536C6.03436 13.201 6.21446 13.0478 6.39386 12.8939C8.00841 11.5084 9.56569 10.0657 11.0657 8.56569C11.3857 8.24569 11.3857 7.75431 11.0657 7.43431C9.56569 5.93431 8.00841 4.49159 6.39386 3.10614C6.21446 2.9522 6.03436 2.79897 5.85355 2.64645Z" fill="#2B2B2B" />
                                </svg>

                            </a>
                        </div>
                    )}

                </div>
            </div>
        </section>
    );
}
